import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DesignerPageContext } from '../../DesignerPage';
import './CircularSlider.scss';

const CircularSlider = ({
  size = 80,
  strokeWidth = 4,
  onAngleChange,
  onAngleChangeStart,
  initialAngle = 0,
  disabled = false,
  snapToAngles = true,
  snapStep = 90,
  snapThreshold = 20,
  showTicks = true,
  showValue = false,
  className = '',
  modelRef,
}) => {
  const designerPageContext = useContext(DesignerPageContext);

  const radius = (size - strokeWidth - 12) / 2;
  const center = size / 2;
  const [angle, setAngle] = useState(initialAngle);
  const [dragging, setDragging] = useState(false);
  const svgRef = useRef(null);
  const lastValidAngleRef = useRef(initialAngle);

  const polarToCartesian = useCallback(
    (deg, r = radius) => {
      const rad = ((deg - 90) * Math.PI) / 180;
      return {
        x: center + r * Math.cos(rad),
        y: center + r * Math.sin(rad),
      };
    },
    [radius, center]
  );

  const cartesianToAngle = useCallback((x, y) => {
    const rawAngle = Math.atan2(y, x) * (180 / Math.PI);
    return (rawAngle + 450) % 360;
  }, []);

  const renderTicks = useCallback(() => {
    if (!showTicks) return null;

    const ticks = [];
    for (let deg = 0; deg < 360; deg += snapStep) {
      const inner = polarToCartesian(deg, radius - 4);
      const outer = polarToCartesian(deg, radius + 2);

      // Highlight major ticks (every 90 degrees)
      const isMajor = deg % 90 === 0;

      ticks.push(
        <line
          key={deg}
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
          stroke={isMajor ? '#666' : '#aaa'}
          strokeWidth={isMajor ? '3' : '2'}
        />
      );
    }
    return ticks;
  }, [showTicks, snapStep, polarToCartesian, radius]);

  const arcPath = useCallback(
    currentAngle => {
      const start = polarToCartesian(0);
      const end = polarToCartesian(currentAngle);
      const largeArc = currentAngle > 180 ? 1 : 0;

      return `
      M ${start.x} ${start.y}
      A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}
    `;
    },
    [polarToCartesian, radius]
  );

  const handleMouseMove = useCallback(
    e => {
      if (!dragging || !svgRef.current || disabled) return;

      const rect = svgRef.current.getBoundingClientRect();
      const dx = e.clientX - rect.left - center;
      const dy = e.clientY - rect.top - center;
      let newAngle = cartesianToAngle(dx, dy);

      // Snap to angles if enabled
      if (snapToAngles) {
        const nearestSnap = Math.round(newAngle / snapStep) * snapStep;
        if (Math.abs(newAngle - nearestSnap) < snapThreshold) {
          newAngle = nearestSnap % 360;
        }
      }

      // Ensure angle is always between 0 and 360
      newAngle = ((newAngle % 360) + 360) % 360;

      setAngle(newAngle);
      lastValidAngleRef.current = newAngle;

      if (onAngleChange) {
        onAngleChange(newAngle);
      }
    },
    [
      dragging,
      disabled,
      center,
      cartesianToAngle,
      snapToAngles,
      snapStep,
      snapThreshold,
      onAngleChange,
    ]
  );

  const handleMouseDown = useCallback(
    e => {
      if (disabled) return;

      e.preventDefault();
      setDragging(true);

      //Xoá border của model được drag
      designerPageContext.sceneManager?.handleDrawBorder(modelRef.current);

      // Set initial angle based on click position
      const rect = svgRef.current.getBoundingClientRect();
      const dx = e.clientX - rect.left - center;
      const dy = e.clientY - rect.top - center;
      let clickAngle = cartesianToAngle(dx, dy);

      if (snapToAngles) {
        const nearestSnap = Math.round(clickAngle / snapStep) * snapStep;
        if (Math.abs(clickAngle - nearestSnap) < snapThreshold) {
          clickAngle = nearestSnap % 360;
        }
      }

      clickAngle = ((clickAngle % 360) + 360) % 360;

      setAngle(clickAngle);
      lastValidAngleRef.current = clickAngle;

      if (onAngleChange) {
        onAngleChange(clickAngle);
      }
      if (onAngleChangeStart) {
        onAngleChangeStart();
      }
    },
    [
      disabled,
      center,
      cartesianToAngle,
      snapToAngles,
      snapStep,
      snapThreshold,
      onAngleChange,
      onAngleChangeStart,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);

    //Tự động xoay module về các góc thoả mãn
    const snapPoints = [0, 90, 180, 270, 360];
    const snappedAngle = snapPoints.reduce((prev, curr) =>
      Math.abs(curr - angle) < Math.abs(prev - angle) ? curr : prev
    );

    setAngle(snappedAngle);

    if (onAngleChange) {
      onAngleChange(snappedAngle);
    }
  }, [angle]);

  // Handle touch events for mobile support
  const handleTouchMove = useCallback(
    e => {
      if (!dragging || !svgRef.current || disabled) return;

      e.preventDefault();
      const touch = e.touches[0];
      const rect = svgRef.current.getBoundingClientRect();
      const dx = touch.clientX - rect.left - center;
      const dy = touch.clientY - rect.top - center;
      let newAngle = cartesianToAngle(dx, dy);

      if (snapToAngles) {
        const nearestSnap = Math.round(newAngle / snapStep) * snapStep;
        if (Math.abs(newAngle - nearestSnap) < snapThreshold) {
          newAngle = nearestSnap % 360;
        }
      }

      newAngle = ((newAngle % 360) + 360) % 360;

      setAngle(newAngle);
      lastValidAngleRef.current = newAngle;

      if (onAngleChange) {
        onAngleChange(newAngle);
      }
    },
    [
      dragging,
      disabled,
      center,
      cartesianToAngle,
      snapToAngles,
      snapStep,
      snapThreshold,
      onAngleChange,
    ]
  );

  const handleTouchStart = useCallback(
    e => {
      if (disabled) return;

      e.preventDefault();
      setDragging(true);

      const touch = e.touches[0];
      const rect = svgRef.current.getBoundingClientRect();
      const dx = touch.clientX - rect.left - center;
      const dy = touch.clientY - rect.top - center;
      let clickAngle = cartesianToAngle(dx, dy);

      if (snapToAngles) {
        const nearestSnap = Math.round(clickAngle / snapStep) * snapStep;
        if (Math.abs(clickAngle - nearestSnap) < snapThreshold) {
          clickAngle = nearestSnap % 360;
        }
      }

      clickAngle = ((clickAngle % 360) + 360) % 360;

      setAngle(clickAngle);
      lastValidAngleRef.current = clickAngle;

      if (onAngleChange) {
        onAngleChange(clickAngle);
      }
    },
    [
      disabled,
      center,
      cartesianToAngle,
      snapToAngles,
      snapStep,
      snapThreshold,
      onAngleChange,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    setDragging(false);
  }, []);

  // Reset to last valid angle if needed (for collision detection)
  const resetToLastValidAngle = useCallback(() => {
    setAngle(lastValidAngleRef.current);
  }, []);

  // External method to set angle (for programmatic control)
  const setExternalAngle = useCallback(newAngle => {
    const normalizedAngle = ((newAngle % 360) + 360) % 360;
    setAngle(normalizedAngle);
    lastValidAngleRef.current = normalizedAngle;
  }, []);

  // Update angle when initialAngle prop changes
  useEffect(() => {
    const normalizedAngle = ((initialAngle % 360) + 360) % 360;
    setAngle(normalizedAngle);
    lastValidAngleRef.current = normalizedAngle;
  }, [initialAngle]);

  useEffect(() => {
    if (dragging) {
      // Mouse events
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      // Touch events
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [
    dragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Expose methods for parent component
  useEffect(() => {
    if (svgRef.current) {
      svgRef.current.resetToLastValidAngle = resetToLastValidAngle;
      svgRef.current.setExternalAngle = setExternalAngle;
    }
  }, [resetToLastValidAngle, setExternalAngle]);

  const knobPos = polarToCartesian(angle);
  const displayAngle = Math.round(angle);

  return (
    <div
      className={`circular-slider ${className} ${disabled ? 'disabled' : ''}`}
    >
      <svg
        ref={svgRef}
        width={size}
        height={size}
        className={`circular-slider__svg ${dragging ? 'dragging' : ''}`}
      >
        {renderTicks()}

        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#ddd"
          strokeWidth={strokeWidth}
          fill="none"
          className="circular-slider__track"
        />

        {/* Progress arc */}
        <path
          d={arcPath(angle)}
          stroke="#00c6ff"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          className="circular-slider__progress"
        />

        {/* Knob */}
        <circle
          className={`circular-slider__knob ${dragging ? 'dragging' : ''}`}
          cx={knobPos.x}
          cy={knobPos.y}
          r={6}
          fill={disabled ? '#ccc' : '#007bff'}
          stroke="#fff"
          strokeWidth={2}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{ cursor: disabled ? 'not-allowed' : 'grab' }}
        />

        {/* Center dot */}
        <circle
          cx={center}
          cy={center}
          r={2}
          fill="#999"
          className="circular-slider__center"
        />
      </svg>

      {/* Display angle value */}
      {showValue && (
        <div className="circular-slider__value">{displayAngle}°</div>
      )}
    </div>
  );
};

export default CircularSlider;
